import { orderMatchesFilters } from '@features/admin/trading/tradingAdminUtils';
import { useAdminApi } from '@hooks/useAdminApi';
import { useApi } from '@hooks/useApi';
import { useStreamV2Orders } from '@hooks/useStreamV2Orders';
import { useStreamV2Status } from '@hooks/useStreamV2Status';
import { useURLSearchParams } from '@hooks/useURLSearchParams';
import { Filter } from '@protos/filter';
import { Order } from '@protos/v2/order';
import { formatParams } from '@services/ApiUtils';
import { priceFormatterService } from '@services/PriceFormatterService';
import { Status } from '@services/StreamV2';
import { BlotterWidget } from '@shared/components/BlotterWidget';
import { generateEnvBaseURL } from '@utils/url';
import {
  CellClickedEvent,
  CellKeyDownEvent,
  ColumnVisibleEvent,
  FirstDataRenderedEvent,
  GridApi,
  RowClickedEvent,
  RowDoubleClickedEvent,
  SideBarDef,
  ToolPanelVisibleChangedEvent,
} from 'ag-grid-enterprise';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAsync } from 'react-use';
import { TRADING_ADMIN_ORDER_COLUMNS, TRADING_ADMIN_ORDER_FILTERS } from './TradingOrderGridConsts';

const getRowId = ({ data }): string => data?.id?.toString();

const defaultColDef = {
  sortable: true,
  filter: false,
  floatingFilter: false,
  resizable: true,
  width: 150,
  enableCellChangeFlash: true,
};

const generateCSVDownloadUrl = (params?: any) => {
  return `${generateEnvBaseURL()}/orders${params ? formatParams(params) : ''}`;
};

interface TradingOrdersProps {
  hideFilters?: boolean;
  accountId?: string;
  maxHeight?: number;
  selectedProduct?: string;
  enableCSVDownload?: boolean;
  onCellClicked?: (params: CellClickedEvent) => void;
  domLayout?: 'normal' | 'autoHeight';
  filters?: Filter[];
  sideBar?: SideBarDef;
  onColumnVisible?: (event: ColumnVisibleEvent) => void;
  hiddenColumns?: string[];
  onToolPanelVisibilityChanged?: (event: ToolPanelVisibleChangedEvent) => void;
  isAdmin?: boolean;
  limitedToAccounts?: string[];
  isFrozen?: boolean;
  usePagination?: boolean;
  onRowClicked?: (params: RowClickedEvent) => void;
  onRowDoubleClicked?: (params: RowDoubleClickedEvent) => void;
  onCellKeyDown?: (params: CellKeyDownEvent) => void;
  onRowHighlighted?: (order: Order | null) => void;
  onApiAvailable?: (api: GridApi) => void;
}

interface OrdersLoaderParams {
  account_id?: string;
  product_symbol?: string;
}

export const TradingOrdersGrid = ({
  hideFilters = false,
  maxHeight,
  enableCSVDownload = false,
  selectedProduct,
  onCellClicked,
  domLayout = 'autoHeight',
  filters,
  sideBar,
  onColumnVisible,
  hiddenColumns,
  onToolPanelVisibilityChanged,
  isAdmin = true,
  limitedToAccounts,
  isFrozen,
  usePagination,
  onRowClicked,
  onRowDoubleClicked,
  onApiAvailable,
}: TradingOrdersProps) => {
  const { apiClient } = isAdmin ? useAdminApi() : useApi();
  const dataLoaderParams = () => {
    const params: OrdersLoaderParams = {};
    if (selectedProduct) params.product_symbol = selectedProduct.toLowerCase();
    return params;
  };
  const dataLoader = apiClient?.ordersLoader(dataLoaderParams());

  const { urlParams } = useURLSearchParams();

  const blotterApiRef = useRef<GridApi<Order | null> | null>(null);

  const [isReadyToProcessMessages, setIsReadyToProcessMessages] = useState<boolean>(false);
  const [batchedOrders, setBatchedOrders] = useState<Order[]>([]);
  const status = useStreamV2Status();

  useEffect(() => {
    setIsReadyToProcessMessages(status === Status.Connected);
  }, [status]);

  const onGetGridApi = (gridApi: GridApi<Order | null>) => {
    blotterApiRef.current = gridApi;
    if (onApiAvailable) {
      onApiAvailable(gridApi);
    }
  };

  useAsync(async () => {
    if (!blotterApiRef.current || !selectedProduct) return;

    await blotterApiRef.current.setColumnFilterModel('product_symbol', {
      filterType: 'text',
      type: 'startsWith',
      filter: selectedProduct,
    });
    blotterApiRef.current.onFilterChanged();
  }, [blotterApiRef.current, selectedProduct]);

  const onDataRendered = (event: FirstDataRenderedEvent<Order | null, any>) => {
    const api = blotterApiRef.current || event.api;
    if (hiddenColumns && hiddenColumns.length > 0) {
      api.setColumnsVisible(hiddenColumns, false);
    }
    api.autoSizeAllColumns;
  };

  useEffect(() => {
    if (batchedOrders.length > 0 && blotterApiRef.current !== null && !isFrozen) {
      if (blotterApiRef.current === null) return;
      batchedOrders.forEach(order => {
        const isRowInGrid = blotterApiRef.current!.getRowNode(order.id.toString());
        addOrderToGrid(order, blotterApiRef.current!, Boolean(isRowInGrid), order);
      });
      setBatchedOrders([]);
    }
  }, [isFrozen, batchedOrders, blotterApiRef.current]);

  const addOrderToGrid = useCallback(
    (transformedOrder: Order, gridApi: GridApi<Order | null>, isRowInGrid: boolean, order: Order) => {
      gridApi.applyTransaction(isRowInGrid ? { update: [transformedOrder] } : { add: [transformedOrder] });

      setTimeout(() => {
        if (!isRowInGrid) {
          const newRowNode = gridApi.getRowNode(order.id.toString());
          if (newRowNode) gridApi.flashCells({ rowNodes: [newRowNode], fadeDuration: 2000 });
        }
      }, 200);
    },
    [blotterApiRef.current, urlParams, limitedToAccounts]
  );

  const onStreamOrder = useCallback(
    (order: Order) => {
      if (!blotterApiRef.current) return;
      const isRowInGrid = !!blotterApiRef.current.getRowNode(order.id.toString());
      if (!isRowInGrid && !orderMatchesFilters(order, urlParams, limitedToAccounts)) {
        return;
      }
      const transformedOrder: Order = {
        ...order,
        amount: priceFormatterService.removeTrailZeros(order.amount),
        executed_amount: order.executed_amount ? priceFormatterService.removeTrailZeros(order.executed_amount) : '',
        price: order.price ? order.price : '',
        executed_avg_price: order.executed_avg_price ? order.executed_avg_price : '',
      };
      if (isFrozen) {
        setBatchedOrders(prev => [...prev, transformedOrder]);
        return;
      }
      addOrderToGrid(transformedOrder, blotterApiRef.current, isRowInGrid, order);
    },
    [blotterApiRef.current, urlParams, isFrozen, batchedOrders]
  );

  useStreamV2Orders(onStreamOrder, isReadyToProcessMessages);

  if (!dataLoader) return null;

  return (
    <BlotterWidget
      getGridApi={onGetGridApi}
      selectedProduct={selectedProduct}
      dataLoader={dataLoader}
      columnDefs={TRADING_ADMIN_ORDER_COLUMNS}
      getRowId={getRowId}
      defaultColDef={defaultColDef}
      onCellClicked={onCellClicked}
      onFirstDataRendered={onDataRendered}
      animateRows={true}
      domLayout={domLayout}
      autoSizeStrategy={{ type: 'fitCellContents' }}
      {...(hideFilters ? undefined : { filters: filters ?? TRADING_ADMIN_ORDER_FILTERS })}
      maxHeight={maxHeight}
      csvDownloadUrl={enableCSVDownload ? generateCSVDownloadUrl(urlParams) : undefined}
      sideBar={sideBar}
      onColumnVisible={onColumnVisible}
      onToolPanelVisibleChanged={onToolPanelVisibilityChanged}
      usePagination={usePagination}
      gridOptions={{
        rowSelection: {
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        },
      }}
      onRowClicked={onRowClicked}
      onRowDoubleClicked={onRowDoubleClicked}
    />
  );
};
